import { AppErrorBoundary } from '@/design-system'
import { Navigate, Route, useLocation, useNavigate, useParams } from '@solidjs/router'
import {
  type Component,
  type JSX,
  Show,
  Suspense,
  createEffect,
  createSignal,
  lazy,
  on,
  onMount,
} from 'solid-js'
import type { UserType } from './api/types/generated'
import { ActivationGate } from './features/candidate-activation/ActivationGate'
import { preloadProfileHub } from './features/candidate-profile-hub/preload'
import { SURVEY_NOTICE_PATH } from './features/survey-participant/lib/surveyNotice'
import { I18nProvider } from './i18n'
import { DEFAULT_LOCALE, type Locale, SUPPORTED_LOCALES } from './i18n/types'
import { trackPageView } from './posthog'
import { EmployeeRouteGuard } from './shared/components/EmployeeRouteGuard'
import { GuestOnlyRoute } from './shared/components/GuestOnlyRoute'
import { HiringManagerRedirect } from './shared/components/HiringManagerRedirect'
import { ProtectedRoute } from './shared/components/ProtectedRoute'
import { RouteSkeleton } from './shared/components/RouteSkeleton'
import { WorkspaceRouteGuard } from './shared/components/WorkspaceRouteGuard'
import { ConsentBanner } from './shared/consent/ConsentBanner'
import { useAdFreeNavigationGuard } from './shared/hooks/useAdFreeNavigationGuard'
import { useTheme } from './shared/hooks/useTheme'
import { useWorkspaceBrandTheme } from './shared/hooks/useWorkspaceBrandTheme'
import { ensureSession } from './shared/session/ensureSession'
import { captureRefFromUrl } from './shared/utils/referral'
import { getLanguage, setLanguage } from './stores/preferences.store'
import { ToastProvider } from './stores/toast.store'

const AppShell = lazy(() =>
  import('./shared/components/AppShell').then((m) => ({ default: m.AppShell }))
)

const LandingPage = lazy(() => import('./features/landing/LandingPage'))
const PricingPage = lazy(() => import('./features/pricing/PricingPage'))
const PrivacyPage = lazy(() => import('./features/privacy/PrivacyPage'))
const TryNowPage = lazy(() => import('./features/try-now/TryNowPage'))
const TryNowConfirmPage = lazy(() => import('./features/try-now/TryNowConfirmPage'))
const PrivacySummaryPage = lazy(() => import('./features/privacy/PrivacySummaryPage'))
const AIEvaluationNoticePage = lazy(() => import('./features/legal/AIEvaluationNoticePage'))
const SurveyNoticePage = lazy(() => import('./features/survey-participant/SurveyNoticePage'))
const SurveyWelcomePage = lazy(() => import('./features/survey-participant/SurveyWelcomePage'))
const SurveyQuestionsPage = lazy(() => import('./features/survey-participant/SurveyQuestionsPage'))
const SurveyLobbyPage = lazy(() => import('./features/survey-participant/SurveyLobbyPage'))
const SurveyConversationPage = lazy(
  () => import('./features/survey-participant/SurveyConversationPage')
)
const SurveyReceiptPage = lazy(() => import('./features/survey-participant/SurveyReceiptPage'))
const SurveyThanksPage = lazy(() => import('./features/survey-participant/SurveyThanksPage'))
const DPAPage = lazy(() => import('./features/legal/DPAPage'))

const ForCandidatesPage = lazy(() => import('./features/static-pages/ForCandidatesPage'))
const HomePage = lazy(() => import('./features/static-pages/home/HomePage'))
const AboutPage = lazy(() => import('./features/static-pages/AboutPage'))
const ContactPage = lazy(() => import('./features/static-pages/ContactPage'))
const TermsPage = lazy(() => import('./features/static-pages/TermsPage'))
const CookiesPage = lazy(() => import('./features/static-pages/CookiesPage'))
const ChangelogPage = lazy(() => import('./features/changelog/ChangelogPage'))
const FeaturesPage = lazy(() => import('./features/features-page/FeaturesPage'))
const McpPage = lazy(() => import('./features/features-page/McpPage'))
const PendingActionPage = lazy(() => import('./features/pending-action/PendingActionPage'))
const FeatureCategoryPage = lazy(() => import('./features/features-page/FeatureCategoryPage'))

import { PortalBrandProvider } from './features/client-portal/components/PortalBrandProvider'

const ClientSignInPage = lazy(() => import('./features/client-portal/pages/ClientSignInPage'))
const AcceptInvitePage = lazy(() => import('./features/client-portal/pages/AcceptInvitePage'))
const ClientDashboardPage = lazy(() => import('./features/client-portal/pages/ClientDashboardPage'))
const CandidateDetailPage = lazy(() => import('./features/client-portal/pages/CandidateDetailPage'))
const CompareCandidatesPage = lazy(
  () => import('./features/client-portal/pages/CompareCandidatesPage')
)
const ClientBoardPage = lazy(() => import('./features/client-portal/pages/ClientBoardPage'))

const DashboardPage = lazy(() => import('./features/dashboard/DashboardPage'))
const PitchPage = lazy(() => import('./features/pitch-generator/PitchPage'))
const InterviewPage = lazy(() => import('./features/interview-mock/InterviewPage'))
const CandidatePracticeHistoryPage = lazy(
  () => import('./features/interview-history/CandidatePracticeHistoryPage')
)
const CandidateCompanyHistoryPage = lazy(
  () => import('./features/interview-history/CandidateCompanyHistoryPage')
)
const ApplicationDetailPage = lazy(
  () => import('./features/candidate-applications/ApplicationDetailPage')
)
const CandidateReplayPage = lazy(
  () => import('./features/candidate-applications/CandidateReplayPage')
)
const MonitoringPage = lazy(() => import('./features/monitoring/MonitoringPage'))

const CandidateLoginPage = lazy(() =>
  import('./features/auth-unified/authPages').then((m) => ({ default: m.CandidateLoginPage }))
)
const CandidateSignupPage = lazy(() =>
  import('./features/auth-unified/authPages').then((m) => ({ default: m.CandidateSignupPage }))
)
const HiringManagerLoginPage = lazy(() =>
  import('./features/auth-unified/authPages').then((m) => ({
    default: m.HiringManagerLoginPage,
  }))
)
const HiringManagerSignupPage = lazy(() =>
  import('./features/auth-unified/authPages').then((m) => ({
    default: m.HiringManagerSignupPage,
  }))
)

const ForgotPasswordPage = lazy(() =>
  import('./features/auth-shared/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  }))
)
const ResetPasswordPage = lazy(() =>
  import('./features/auth-shared/ResetPasswordPage').then((m) => ({
    default: m.ResetPasswordPage,
  }))
)
const MagicLinkPage = lazy(() =>
  import('./features/auth-shared/MagicLinkPage').then((m) => ({
    default: m.MagicLinkPage,
  }))
)
const VerifyEmailPage = lazy(() =>
  import('./features/auth-shared/VerifyEmailPage').then((m) => ({
    default: m.VerifyEmailPage,
  }))
)
const CreateAccountPage = lazy(() =>
  import('./features/auth-shared/CreateAccountPage').then((m) => ({
    default: m.CreateAccountPage,
  }))
)
const PersonalityRequestPage = lazy(() =>
  import('./features/auth-shared/PersonalityRequestPage').then((m) => ({
    default: m.PersonalityRequestPage,
  }))
)
const PersonalityRequestLanding = lazy(() =>
  import('./features/personality-assessment').then((m) => ({
    default: m.PersonalityRequestLanding,
  }))
)

const EmployeeLoginPage = lazy(() =>
  import('./features/employee').then((m) => ({ default: m.EmployeeLoginPage }))
)
const EmployeeAcceptPage = lazy(() =>
  import('./features/employee').then((m) => ({ default: m.EmployeeAcceptPage }))
)
const EmployeeObjectivesPage = lazy(() =>
  import('./features/employee').then((m) => ({ default: m.EmployeeObjectivesPage }))
)
const CandidateDashboard = lazy(() =>
  import('./features/candidate-dashboard/CandidateDashboard').then((m) => ({
    default: m.CandidateDashboard,
  }))
)
const HiringManagerDashboard = lazy(() =>
  import('./features/hiring-manager-dashboard/HiringManagerDashboard').then((m) => ({
    default: m.HiringManagerDashboard,
  }))
)
const BulkInvitesPage = lazy(() => import('./features/bulk-invites/BulkInvitesPage'))
const CreatePositionPage = lazy(() => import('./features/bulk-invites/CreatePositionPage'))
const CommunityQuestionsPage = lazy(
  () => import('./features/community-questions/CommunityQuestionsPage')
)
const GeneralSettingsPage = lazy(
  () => import('./features/hiring-manager-settings/GeneralSettingsPage')
)
const WorkspaceSettingsPage = lazy(() => import('./features/workspace/WorkspaceSettingsPage'))
const WorkspaceActivityPage = lazy(() => import('./features/workspace/WorkspaceActivityPage'))
const KnowledgeBasePage = lazy(() => import('./features/knowledge-base/KnowledgeBasePage'))
const QuestionDetailPage = lazy(() => import('./features/knowledge-base/QuestionDetailPage'))
const TopicDetailPage = lazy(() => import('./features/topics/TopicDetailPage'))
const EmailTemplateEditorPage = lazy(
  () => import('./features/email-templates/EmailTemplateEditorPage')
)
const TalentPoolPage = lazy(() => import('./features/workspace-candidates/CandidatesPage'))
const MessageHubPage = lazy(() => import('./features/message-hub/MessageHubPage'))
const SurveyCampaignsPage = lazy(() => import('./features/survey-campaigns/SurveyCampaignsPage'))
const SurveyCampaignFormPage = lazy(
  () => import('./features/survey-campaigns/SurveyCampaignFormPage')
)
const SurveyResultsPage = lazy(() => import('./features/survey-campaigns/SurveyResultsPage'))
const BotProfilePage = lazy(() => import('./features/bots/pages/BotProfilePage'))
const BotTeamPage = lazy(() => import('./features/bots/pages/BotTeamPage'))
const CandidateProfilePage = lazy(
  () => import('./features/candidate-profile-page/CandidateProfilePage')
)
const PeoplePage = lazy(() => import('./features/workspace-people/PeoplePage'))
const ReviewMeetingsPage = lazy(() => import('./features/review-meetings/ReviewMeetingsPage'))
const WorkspaceInvitePage = lazy(() => import('./features/workspace/WorkspaceInvitePage'))
const WorkspaceSetupPage = lazy(() => import('./features/workspace/WorkspaceSetupPage'))

const ATSOptimizerPage = lazy(() => import('./features/ats-optimizer/ATSOptimizerPage'))
const InterviewReplayPage = lazy(() => import('./features/interview-replay/InterviewReplayPage'))
const MeetingRecapPage = lazy(() => import('./features/meeting-recap/MeetingRecapPage'))
const NextMeetingBriefPage = lazy(
  () => import('./features/next-meeting-brief/NextMeetingBriefPage')
)
const CompanyMeetingPage = lazy(() =>
  import('./features/meeting-room/CompanyMeetingPage').then((m) => ({
    default: m.CompanyMeetingPage,
  }))
)
const JobTrackerPage = lazy(() => import('./features/job-tracker/JobTrackerPage'))
const JobOffersPage = lazy(() => import('./features/job-offers/JobOffersPage'))
const CandidateNotificationsPage = lazy(
  () => import('./features/candidate-notifications/CandidateNotificationsPage')
)
const JobOfferDetailPage = lazy(() => import('./features/job-offers/JobOfferDetailPage'))
const TailoredCVPage = lazy(() => import('./features/job-offers/TailoredCVPage'))
const PrepDrillPage = lazy(() => import('./features/job-offers/prep/PrepDrillPage'))
const CompanionPage = lazy(() => import('./features/ai-companion/CompanionPage'))
const PersonalityAssessmentPage = lazy(() =>
  import('./features/personality-assessment').then((m) => ({
    default: m.PersonalityAssessmentPage,
  }))
)
const ProfileHubPage = lazy(() =>
  import('./features/candidate-profile-hub').then((m) => ({
    default: m.ProfileHubPage,
  }))
)
const SuperCvPreviewPage = lazy(() => import('./features/candidate-profile-hub/SuperCvPreviewPage'))
const WorkoutPage = lazy(() => import('./features/daily-workout/WorkoutPage'))
const AchievementsPage = lazy(() => import('./features/achievements/AchievementsPage'))
const CandidateGeneralSettingsPage = lazy(
  () => import('./features/candidate-settings/GeneralSettingsPage')
)
const DeleteAccountPage = lazy(() => import('./features/candidate-settings/DeleteAccountPage'))
const ActivationPage = lazy(() => import('./features/candidate-activation/ActivationPage'))
const DiscoveryPage = lazy(() => import('./features/candidate-discovery/DiscoveryPage'))
const FocusPage = lazy(() => import('./features/candidate-focus/FocusPage'))
const JobSoughtPage = lazy(() => import('./features/candidate-job-sought/JobSoughtPage'))
const OAuthConsentPage = lazy(() => import('./features/oauth-consent/OAuthConsentPage'))
const BillingPage = lazy(() =>
  import('./features/billing').then((m) => ({ default: m.BillingPage }))
)
const RecruiterAgentPage = lazy(() =>
  import('./features/recruiter-agent/RecruiterAgentPage').then((m) => ({
    default: m.default,
  }))
)
const IdealProfilesPage = lazy(() =>
  import('./features/ideal-profiles/IdealProfilesPage').then((m) => ({
    default: m.IdealProfilesPage,
  }))
)
const CandidateTrainingPage = lazy(() =>
  import('./features/candidate-training/CandidateTrainingPage').then((m) => ({
    default: m.CandidateTrainingPage,
  }))
)
const CreateTrainingPage = lazy(() => import('./features/candidate-training/CreateTrainingPage'))
const TrainingSessionPage = lazy(() => import('./features/candidate-training/TrainingSessionPage'))
const InterviewProfilePage = lazy(() =>
  import('./features/interview-profile/InterviewProfilePage').then((m) => ({
    default: m.InterviewProfilePage,
  }))
)
const CoachingPathPage = lazy(() =>
  import('./features/coaching-path/CoachingPathPage').then((m) => ({
    default: m.CoachingPathPage,
  }))
)
const ObjectivesPage = lazy(() => import('./features/objectives/ObjectivesPage'))
const MarketplaceComingSoon = lazy(() =>
  import('./features/marketplace/MarketplaceComingSoon').then((m) => ({
    default: m.MarketplaceComingSoon,
  }))
)
const IntelligenceDashboardPage = lazy(() =>
  import('./features/intelligence-dashboard/IntelligenceDashboardPage').then((m) => ({
    default: m.IntelligenceDashboardPage,
  }))
)
const ResumeAlignmentPage = lazy(() =>
  import('./features/resume-alignment/ResumeAlignmentPage').then((m) => ({
    default: m.ResumeAlignmentPage,
  }))
)
const SnakePage = lazy(() => import('./features/snake/SnakePage'))

const RefereePage = lazy(() => import('./features/recommendations/pages/RefereePage'))
const EmailEntryPage = lazy(() => import('./features/shared-interview/pages/EmailEntryPage'))
const LobbyPage = lazy(() => import('./features/shared-interview/pages/LobbyPage'))
const ReadyPage = lazy(() => import('./features/shared-interview/pages/ReadyPage'))
const SharedInterviewPage = lazy(() => import('./features/shared-interview/pages/InterviewPage'))
const ThankYouPage = lazy(() => import('./features/shared-interview/pages/ThankYouPage'))

const ApplyPage = lazy(() => import('./features/public-apply/ApplyPage'))

const CompanySpacePage = lazy(() => import('./features/company-space/CompanySpacePage'))
const PublicCVPage = lazy(() => import('./features/public-cv/PublicCVPage'))

const BookingPage = lazy(() =>
  import('./features/scheduling/BookingPage').then((m) => ({ default: m.BookingPage }))
)

const CandidateMeetingPage = lazy(() =>
  import('./features/meeting-room/CandidateMeetingPage').then((m) => ({
    default: m.CandidateMeetingPage,
  }))
)

const AdminLoginPage = lazy(() =>
  import('./features/admin/AdminLoginPage').then((m) => ({ default: m.AdminLoginPage }))
)
const AdminDashboardPage = lazy(() =>
  import('./features/admin/AdminDashboardPage').then((m) => ({ default: m.AdminDashboardPage }))
)
const AdminUsersPage = lazy(() => import('./features/admin/pages/AdminUsersPage'))
const AdminWorkspacesPage = lazy(() => import('./features/admin/pages/AdminWorkspacesPage'))
const AdminInvitesPage = lazy(() => import('./features/admin/pages/AdminInvitesPage'))
const FounderInviteAcceptPage = lazy(() => import('./features/auth/FounderInviteAcceptPage'))
const ClaimDemoPage = lazy(() => import('./features/partner-demo/ClaimDemoPage'))
const AdminWorkspaceDetailPage = lazy(
  () => import('./features/admin/pages/AdminWorkspaceDetailPage')
)
const AdminManagementPage = lazy(() => import('./features/admin/pages/AdminManagementPage'))
const AdminHealthPage = lazy(() => import('./features/admin/pages/AdminHealthPage'))
const AdminScoringPage = lazy(() => import('./features/admin/pages/AdminScoringPage'))
const AdminLiveKitPage = lazy(() => import('./features/admin/pages/AdminLiveKitPage'))
const AdminVoiceEnginePage = lazy(() => import('./features/admin/pages/AdminVoiceEnginePage'))
const AdminEmailTemplatesPage = lazy(() => import('./features/admin/pages/AdminEmailTemplatesPage'))
const AdminEmailLogsPage = lazy(() => import('./features/admin/pages/AdminEmailLogsPage'))
const AdminPromptsPage = lazy(() => import('./features/admin/pages/AdminPromptsPage'))
const AdminTranslationsPage = lazy(() => import('./features/admin/pages/AdminTranslationsPage'))
const AdminSupportPage = lazy(() => import('./features/admin/pages/AdminSupportPage'))
const AdminDesignSystemPage = lazy(() => import('./features/admin/pages/AdminDesignSystemPage'))
const AdminStoragePage = lazy(() => import('./features/admin/pages/AdminStoragePage'))
const AdminPartnerKeysPage = lazy(() => import('./features/admin/pages/AdminPartnerKeysPage'))
const AdminDemoPage = lazy(() => import('./features/admin/pages/AdminDemoPage'))
const AdminSessionReplayPage = lazy(() => import('./features/admin/pages/AdminSessionReplayPage'))
const AdminRemindersPage = lazy(() => import('./features/admin/pages/AdminRemindersPage'))
const AdminCertificationsPage = lazy(() => import('./features/admin/pages/AdminCertificationsPage'))
const AdminTrialRequestsPage = lazy(() => import('./features/admin/pages/AdminTrialRequestsPage'))
const AdminPlatformSettingsPage = lazy(
  () => import('./features/admin/pages/AdminPlatformSettingsPage')
)
const AdminOnboardingPage = lazy(() => import('./features/admin/pages/AdminOnboardingPage'))
const NotFoundPage = lazy(() => import('./features/not-found/NotFoundPage'))

const PageLoader: Component = () => <RouteSkeleton />

const ProtectedAdminRoute: Component<{ children: JSX.Element }> = (props) => {
  const navigate = useNavigate()
  const [isChecking, setIsChecking] = createSignal(true)
  const [isAuthed, setIsAuthed] = createSignal(false)

  onMount(async () => {
    const { initAdminAuth, isAdminAuthenticated } = await import(
      './features/admin/stores/admin.store'
    )
    await initAdminAuth()
    if (!isAdminAuthenticated()) {
      navigate('/admin/login', { replace: true })
    } else {
      setIsAuthed(true)
    }
    setIsChecking(false)
  })

  return (
    <Show when={!isChecking() && isAuthed()} fallback={<PageLoader />}>
      <AppErrorBoundary>{props.children}</AppErrorBoundary>
    </Show>
  )
}

type PreloadableComponent = Component & { preload?: () => Promise<unknown> }

const SetupRedirect: Component = () => {
  const location = useLocation()
  return <Navigate href={location.pathname.replace(/\/setup\/?$/, '/lobby')} />
}

const StandalonePage = (Page: PreloadableComponent): Component => {
  return () => {
    void Page.preload?.()
    return (
      <I18nProvider>
        <ToastProvider>
          <AppErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Page />
            </Suspense>
          </AppErrorBoundary>
        </ToastProvider>
      </I18nProvider>
    )
  }
}

const PortalLayout: Component<{ children?: JSX.Element }> = (props) => (
  <I18nProvider>
    <ToastProvider>
      <AppErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <PortalBrandProvider>{props.children}</PortalBrandProvider>
        </Suspense>
      </AppErrorBoundary>
    </ToastProvider>
  </I18nProvider>
)

const GuestOnlyPage = (
  Page: PreloadableComponent,
  userType: 'candidate' | 'hiring_manager'
): Component => {
  return () => {
    void Page.preload?.()
    return (
      <I18nProvider>
        <ToastProvider>
          <GuestOnlyRoute userType={userType}>
            <AppErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Page />
              </Suspense>
            </AppErrorBoundary>
          </GuestOnlyRoute>
        </ToastProvider>
      </I18nProvider>
    )
  }
}

const withEmployeeLayout = (Page: PreloadableComponent): Component => {
  return () => {
    void Page.preload?.()
    return (
      <I18nProvider>
        <ToastProvider>
          <EmployeeRouteGuard>
            <AppErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Page />
              </Suspense>
            </AppErrorBoundary>
          </EmployeeRouteGuard>
        </ToastProvider>
      </I18nProvider>
    )
  }
}

const withProtectedLayout = (
  Page: PreloadableComponent,
  options?: { gated?: boolean; requiredUserType?: UserType }
): Component => {
  const Body: Component = () => (
    <Suspense fallback={<PageLoader />}>
      <Page />
    </Suspense>
  )
  return () => {
    void Page.preload?.()
    return (
      <I18nProvider>
        <ToastProvider>
          <ProtectedRoute requiredUserType={options?.requiredUserType}>
            <Suspense fallback={<PageLoader />}>
              <AppShell>
                {options?.gated ? (
                  <ActivationGate>
                    <Body />
                  </ActivationGate>
                ) : (
                  <Body />
                )}
              </AppShell>
            </Suspense>
          </ProtectedRoute>
        </ToastProvider>
      </I18nProvider>
    )
  }
}

const withAuthLayout = (
  Page: PreloadableComponent,
  requiredUserType?: UserType,
  options?: { gated?: boolean; preloadData?: () => void }
): Component => {
  const Body: Component = () => (
    <Suspense fallback={<PageLoader />}>
      <Page />
    </Suspense>
  )
  return () => {
    void Page.preload?.()
    ensureSession({ requiredUserType, preloadData: options?.preloadData })
    return (
      <I18nProvider>
        <ToastProvider>
          <ProtectedRoute requiredUserType={requiredUserType}>
            <Suspense fallback={<PageLoader />}>
              <AppShell>
                {options?.gated ? (
                  <ActivationGate>
                    <Body />
                  </ActivationGate>
                ) : (
                  <Body />
                )}
              </AppShell>
            </Suspense>
          </ProtectedRoute>
        </ToastProvider>
      </I18nProvider>
    )
  }
}

const withWorkspaceLayout = (Page: PreloadableComponent): Component => {
  return () => {
    void Page.preload?.()
    ensureSession({ requiredUserType: 'hiring_manager' })
    return (
      <I18nProvider>
        <ToastProvider>
          <ProtectedRoute requiredUserType="hiring_manager">
            <WorkspaceRouteGuard>
              <Suspense fallback={<PageLoader />}>
                <AppShell>
                  <Suspense fallback={<PageLoader />}>
                    <Page />
                  </Suspense>
                </AppShell>
              </Suspense>
            </WorkspaceRouteGuard>
          </ProtectedRoute>
        </ToastProvider>
      </I18nProvider>
    )
  }
}

const HiringManagerProfileRedirect: Component = () => {
  const params = useParams<{ workspaceSlug: string }>()
  return <Navigate href={`/app/w/${params.workspaceSlug}/settings/general`} />
}

const WorkspaceRootRedirect: Component = () => {
  const params = useParams<{ workspaceSlug: string }>()
  return <Navigate href={`/app/w/${params.workspaceSlug}/dashboard`} />
}

const EmployeeSlugRedirect: Component = () => {
  const params = useParams<{ slug: string }>()
  return <Navigate href={`/app/employee/${params.slug}/objectives`} />
}

const withWorkspaceFullscreen = (Page: PreloadableComponent): Component => {
  return () => {
    void Page.preload?.()
    return (
      <I18nProvider>
        <ToastProvider>
          <ProtectedRoute requiredUserType="hiring_manager">
            <WorkspaceRouteGuard>
              <Suspense fallback={<PageLoader />}>
                <Page />
              </Suspense>
            </WorkspaceRouteGuard>
          </ProtectedRoute>
        </ToastProvider>
      </I18nProvider>
    )
  }
}

const withCandidateFullscreen = (Page: PreloadableComponent): Component => {
  return () => {
    void Page.preload?.()
    ensureSession({ requiredUserType: 'candidate' })
    return (
      <I18nProvider>
        <ToastProvider>
          <ProtectedRoute requiredUserType="candidate">
            <Suspense fallback={<PageLoader />}>
              <Page />
            </Suspense>
          </ProtectedRoute>
        </ToastProvider>
      </I18nProvider>
    )
  }
}

const withHiringManagerFullscreen = (Page: PreloadableComponent): Component => {
  return () => {
    void Page.preload?.()
    ensureSession({ requiredUserType: 'hiring_manager' })
    return (
      <I18nProvider>
        <ToastProvider>
          <ProtectedRoute requiredUserType="hiring_manager">
            <Suspense fallback={<PageLoader />}>
              <Page />
            </Suspense>
          </ProtectedRoute>
        </ToastProvider>
      </I18nProvider>
    )
  }
}

const LOCALE_PREFIXES = SUPPORTED_LOCALES.filter((locale) => locale !== DEFAULT_LOCALE)

// Marketing pages are crawled at a distinct URL per language (/pricing, /es/pricing) because
// hreflang only pairs pages that live at different URLs. Serving both languages from one URL
// would make the pair uncrawlable and drop the Spanish pages out of LATAM results.
export const localizedPaths = (path: string): string[] => [
  path,
  ...LOCALE_PREFIXES.map((locale) => `/${locale}${path === '/' ? '' : path}`),
]

const localeFromPath = (pathname: string): Locale | null => {
  for (const locale of LOCALE_PREFIXES) {
    if (pathname === `/${locale}` || pathname.startsWith(`/${locale}/`)) {
      return locale
    }
  }
  return null
}

const AppRoot: Component<{ children?: JSX.Element }> = (props) => {
  useAdFreeNavigationGuard()
  useTheme()
  useWorkspaceBrandTheme()

  onMount(() => {
    captureRefFromUrl()
  })

  const location = useLocation()
  createEffect(
    on(
      () => location.pathname,
      (path) => {
        trackPageView(path)
        const fromPath = localeFromPath(path)
        if (fromPath && getLanguage() !== fromPath) {
          setLanguage(fromPath)
        }
      }
    )
  )

  return (
    <>
      {props.children}

      <ConsentBanner />
    </>
  )
}

const RetiredHiringPage: Component = () => {
  const location = useLocation()
  const language = localeFromPath(location.pathname)
  return (
    <Navigate
      href={`${language && language !== 'en' ? `/${language}` : '/'}${location.search}${location.hash}`}
    />
  )
}

const AppRoutes: Component = () => {
  return (
    <>
      <Route path={localizedPaths('/')} component={StandalonePage(HomePage)} />
      <Route path={localizedPaths('/for-hiring-teams')} component={RetiredHiringPage} />
      <Route path="/how-it-works" component={StandalonePage(LandingPage)} />
      <Route path={localizedPaths('/pricing')} component={StandalonePage(PricingPage)} />
      <Route path={localizedPaths('/privacy')} component={StandalonePage(PrivacyPage)} />
      <Route path={localizedPaths('/try')} component={StandalonePage(TryNowPage)} />
      <Route
        path={localizedPaths('/try/confirm/:token')}
        component={StandalonePage(TryNowConfirmPage)}
      />
      <Route
        path={localizedPaths('/privacy-summary')}
        component={StandalonePage(PrivacySummaryPage)}
      />
      <Route path="/ai-evaluation" component={StandalonePage(AIEvaluationNoticePage)} />
      <Route path={SURVEY_NOTICE_PATH} component={StandalonePage(SurveyNoticePage)} />
      <Route path={localizedPaths('/dpa')} component={StandalonePage(DPAPage)} />

      <Route
        path={localizedPaths('/for-candidates')}
        component={StandalonePage(ForCandidatesPage)}
      />
      <Route path={localizedPaths('/for-hiring-managers')} component={RetiredHiringPage} />
      <Route path="/about" component={StandalonePage(AboutPage)} />
      <Route path={localizedPaths('/contact')} component={StandalonePage(ContactPage)} />
      <Route path={localizedPaths('/terms')} component={StandalonePage(TermsPage)} />
      <Route path="/cookies" component={StandalonePage(CookiesPage)} />
      <Route path="/changelog" component={StandalonePage(ChangelogPage)} />
      <Route path={localizedPaths('/features')} component={StandalonePage(FeaturesPage)} />
      <Route path={localizedPaths('/features/mcp')} component={StandalonePage(McpPage)} />
      <Route
        path={localizedPaths('/features/:categoryId')}
        component={StandalonePage(FeatureCategoryPage)}
      />

      <Route path="/client" component={PortalLayout}>
        <Route
          path="/sign-in"
          component={ClientSignInPage}
          preload={() => ClientSignInPage.preload?.()}
        />
        <Route
          path="/accept"
          component={AcceptInvitePage}
          preload={() => AcceptInvitePage.preload?.()}
        />
        <Route
          path="/dashboard"
          component={ClientDashboardPage}
          preload={() => ClientDashboardPage.preload?.()}
        />
        <Route
          path="/candidate/:shareId"
          component={CandidateDetailPage}
          preload={() => CandidateDetailPage.preload?.()}
        />
        <Route
          path="/compare"
          component={CompareCandidatesPage}
          preload={() => CompareCandidatesPage.preload?.()}
        />
        <Route
          path="/board"
          component={ClientBoardPage}
          preload={() => ClientBoardPage.preload?.()}
        />
      </Route>

      <Route path="/app" component={() => <Navigate href="/app/dashboard" />} />
      <Route
        path="/app/dashboard"
        component={withProtectedLayout(DashboardPage, {
          gated: true,
          requiredUserType: 'candidate',
        })}
      />
      <Route
        path="/app/interview"
        component={withProtectedLayout(InterviewPage, { gated: true })}
      />
      <Route path="/app/pitch" component={withProtectedLayout(PitchPage, { gated: true })} />
      <Route
        path="/app/history"
        component={() => <Navigate href="/app/hiring-manager/interview-library" />}
      />
      <Route path="/app/monitoring" component={withProtectedLayout(MonitoringPage)} />
      <Route
        path="/app/community/questions"
        component={withProtectedLayout(CommunityQuestionsPage, { gated: true })}
      />
      <Route
        path="/app/topics"
        component={() => <Navigate href="/app/hiring-manager/interview-library" />}
      />

      <Route
        path="/app/candidate/login"
        component={GuestOnlyPage(CandidateLoginPage, 'candidate')}
      />
      <Route
        path="/app/candidate/signup"
        component={GuestOnlyPage(CandidateSignupPage, 'candidate')}
      />
      <Route
        path="/app/hiring-manager/login"
        component={GuestOnlyPage(HiringManagerLoginPage, 'hiring_manager')}
      />
      <Route
        path="/app/hiring-manager/signup"
        component={GuestOnlyPage(HiringManagerSignupPage, 'hiring_manager')}
      />

      <Route path="/app/auth/forgot-password" component={StandalonePage(ForgotPasswordPage)} />
      <Route path="/app/auth/reset-password" component={StandalonePage(ResetPasswordPage)} />
      <Route path="/app/auth/magic-link" component={StandalonePage(MagicLinkPage)} />
      <Route path="/app/auth/verify-email" component={StandalonePage(VerifyEmailPage)} />
      <Route path="/app/auth/create-account" component={StandalonePage(CreateAccountPage)} />
      <Route
        path="/app/auth/personality-request"
        component={StandalonePage(PersonalityRequestPage)}
      />

      <Route path="/auth/forgot-password" component={StandalonePage(ForgotPasswordPage)} />
      <Route path="/auth/reset-password" component={StandalonePage(ResetPasswordPage)} />
      <Route path="/auth/magic-link" component={StandalonePage(MagicLinkPage)} />
      <Route path="/auth/verify-email" component={StandalonePage(VerifyEmailPage)} />
      <Route path="/auth/create-account" component={StandalonePage(CreateAccountPage)} />
      <Route path="/auth/personality-request" component={StandalonePage(PersonalityRequestPage)} />

      <Route
        path="/app/candidate/dashboard"
        component={withAuthLayout(CandidateDashboard, 'candidate', { gated: true })}
      />

      <Route
        path="/app/candidate/profile"
        component={() => <Navigate href="/app/candidate/profile-hub" />}
      />
      <Route
        path="/app/candidate/history"
        component={withAuthLayout(CandidateCompanyHistoryPage, 'candidate', { gated: true })}
      />

      <Route
        path="/app/candidate/applications/:inviteId/replay"
        component={withAuthLayout(CandidateReplayPage, 'candidate')}
      />
      <Route
        path="/app/candidate/applications/:inviteId"
        component={withAuthLayout(ApplicationDetailPage, 'candidate')}
      />
      <Route
        path="/app/candidate/practice-history"
        component={withAuthLayout(CandidatePracticeHistoryPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/ats-optimizer"
        component={withAuthLayout(ATSOptimizerPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/job-tracker"
        component={withAuthLayout(JobTrackerPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/job-offers/:id/prep/:drill"
        component={withAuthLayout(PrepDrillPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/job-offers/:id/cv"
        component={withAuthLayout(TailoredCVPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/job-offers/:id"
        component={withAuthLayout(JobOfferDetailPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/job-offers"
        component={withAuthLayout(JobOffersPage, 'candidate', { gated: true })}
      />

      <Route
        path="/app/candidate/notifications"
        component={withAuthLayout(CandidateNotificationsPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/companion"
        component={withAuthLayout(CompanionPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/personality-assessment"
        component={withAuthLayout(PersonalityAssessmentPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/personality-request/:id"
        component={withAuthLayout(PersonalityRequestLanding, 'candidate', { gated: false })}
      />
      <Route
        path="/app/candidate/profile-hub"
        component={withAuthLayout(ProfileHubPage, 'candidate', {
          gated: true,
          preloadData: preloadProfileHub,
        })}
      />
      <Route
        path="/app/candidate/profile-hub/super-cv/preview"
        component={withAuthLayout(SuperCvPreviewPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/workout"
        component={withAuthLayout(WorkoutPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/achievements"
        component={withAuthLayout(AchievementsPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/coaching"
        component={withAuthLayout(CoachingPathPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/objectives"
        component={withAuthLayout(ObjectivesPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/interview-profile"
        component={withAuthLayout(InterviewProfilePage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/mock-interview"
        component={withAuthLayout(InterviewPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/training/new"
        component={withAuthLayout(CreateTrainingPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/training/session/:sessionId"
        component={withAuthLayout(TrainingSessionPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/training"
        component={withAuthLayout(CandidateTrainingPage, 'candidate', { gated: true })}
      />
      <Route
        path="/app/candidate/resume-alignment"
        component={withAuthLayout(ResumeAlignmentPage, 'candidate', { gated: true })}
      />
      <Route path="/app/marketplace" component={withAuthLayout(MarketplaceComingSoon)} />

      <Route
        path="/app/candidate/settings/general"
        component={withAuthLayout(CandidateGeneralSettingsPage, 'candidate')}
      />
      <Route
        path="/app/candidate/settings/delete"
        component={withAuthLayout(DeleteAccountPage, 'candidate')}
      />
      <Route path="/app/candidate/billing" component={withAuthLayout(BillingPage, 'candidate')} />
      <Route
        path="/app/candidate/snake"
        component={withAuthLayout(SnakePage, 'candidate', { gated: true })}
      />

      <Route
        path="/app/candidate/activate"
        component={withAuthLayout(ActivationPage, 'candidate')}
      />
      <Route path="/app/candidate/job-sought" component={withCandidateFullscreen(JobSoughtPage)} />
      <Route path="/app/candidate/discovery" component={withCandidateFullscreen(DiscoveryPage)} />
      <Route path="/app/candidate/focus" component={withCandidateFullscreen(FocusPage)} />

      <Route path="/app/oauth/consent" component={withHiringManagerFullscreen(OAuthConsentPage)} />

      <Route
        path="/app/w/:workspaceSlug/dashboard"
        component={withWorkspaceLayout(HiringManagerDashboard)}
      />
      <Route path="/app/w/:workspaceSlug/profile" component={HiringManagerProfileRedirect} />
      <Route path="/app/w/:workspaceSlug/people" component={withWorkspaceLayout(PeoplePage)} />
      <Route
        path="/app/w/:workspaceSlug/reviews"
        component={withWorkspaceLayout(ReviewMeetingsPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/positions/new"
        component={withWorkspaceLayout(CreatePositionPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/positions/:positionSlug?/:tab?"
        component={withWorkspaceLayout(BulkInvitesPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/confirm/:id"
        component={withAuthLayout(PendingActionPage, 'hiring_manager')}
      />
      <Route
        path="/app/w/:workspaceSlug/settings/general"
        component={withWorkspaceLayout(GeneralSettingsPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/analytics/:sessionId"
        component={withWorkspaceLayout(InterviewReplayPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/prep/:sessionId"
        component={withWorkspaceLayout(NextMeetingBriefPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/meetings/:recapId"
        component={withWorkspaceLayout(MeetingRecapPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/meet/:inviteId"
        component={withWorkspaceFullscreen(CompanyMeetingPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/recruiter-agent"
        component={withWorkspaceLayout(RecruiterAgentPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/workspace"
        component={withWorkspaceLayout(WorkspaceSettingsPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/ideal-profiles"
        component={withWorkspaceLayout(IdealProfilesPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/intelligence"
        component={withWorkspaceLayout(IntelligenceDashboardPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/activity"
        component={withWorkspaceLayout(WorkspaceActivityPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/interview-library/questions/:id"
        component={withWorkspaceLayout(QuestionDetailPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/interview-library/topics/:id"
        component={withWorkspaceLayout(TopicDetailPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/interview-library/emails/:id"
        component={withWorkspaceLayout(EmailTemplateEditorPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/interview-library"
        component={withWorkspaceLayout(KnowledgeBasePage)}
      />
      <Route
        path="/app/w/:workspaceSlug/talent-pool"
        component={withWorkspaceLayout(TalentPoolPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/messages"
        component={withWorkspaceLayout(MessageHubPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/feedback"
        component={withWorkspaceLayout(SurveyCampaignsPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/feedback/new"
        component={withWorkspaceLayout(SurveyCampaignFormPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/feedback/:batchId/edit"
        component={withWorkspaceLayout(SurveyCampaignFormPage)}
      />
      <Route
        path="/app/w/:workspaceSlug/feedback/:batchId/:tab?"
        component={withWorkspaceLayout(SurveyResultsPage)}
      />
      <Route path="/app/w/:workspaceSlug/bots" component={withWorkspaceLayout(BotTeamPage)} />
      <Route
        path="/app/w/:workspaceSlug/bots/:botKey"
        component={withWorkspaceLayout(BotProfilePage)}
      />
      <Route
        path="/app/w/:workspaceSlug/candidates/:email"
        component={withWorkspaceLayout(CandidateProfilePage)}
      />

      <Route
        path="/app/w/:workspaceSlug/knowledge-base"
        component={() => <Navigate href="../interview-library" />}
      />

      <Route
        path="/app/hiring-manager/login"
        component={GuestOnlyPage(HiringManagerLoginPage, 'hiring_manager')}
      />
      <Route
        path="/app/hiring-manager/signup"
        component={GuestOnlyPage(HiringManagerSignupPage, 'hiring_manager')}
      />
      <Route path="/app/hiring-manager/*" component={HiringManagerRedirect} />

      <Route path="/app/w/create" component={StandalonePage(WorkspaceSetupPage)} />

      <Route path="/app/w/:workspaceSlug" component={WorkspaceRootRedirect} />

      <Route path="/app/employee/login" component={StandalonePage(EmployeeLoginPage)} />
      <Route path="/app/employee/accept/:token" component={StandalonePage(EmployeeAcceptPage)} />
      <Route
        path="/app/employee/:slug/objectives"
        component={withEmployeeLayout(EmployeeObjectivesPage)}
      />
      <Route path="/app/employee/:slug" component={EmployeeSlugRedirect} />

      <Route path="/app/workspace/invite/:token" component={StandalonePage(WorkspaceInvitePage)} />

      <Route path="/recommend/:token" component={StandalonePage(RefereePage)} />
      <Route path="/feedback/:token" component={StandalonePage(SurveyWelcomePage)} />
      <Route path="/feedback/:token/questions" component={StandalonePage(SurveyQuestionsPage)} />
      <Route path="/feedback/:token/lobby" component={StandalonePage(SurveyLobbyPage)} />
      <Route path="/feedback/:token/talk" component={StandalonePage(SurveyConversationPage)} />
      <Route path="/feedback/:token/receipt" component={StandalonePage(SurveyReceiptPage)} />
      <Route path="/feedback/:token/thanks" component={StandalonePage(SurveyThanksPage)} />
      <Route path="/f/:linkToken" component={StandalonePage(SurveyWelcomePage)} />
      <Route path="/f/:linkToken/questions" component={StandalonePage(SurveyQuestionsPage)} />
      <Route path="/f/:linkToken/lobby" component={StandalonePage(SurveyLobbyPage)} />

      <Route path="/interview/shared/:token" component={StandalonePage(EmailEntryPage)} />
      <Route path="/interview/shared/:token/ready" component={StandalonePage(ReadyPage)} />
      <Route path="/interview/shared/:token/setup" component={SetupRedirect} />
      <Route path="/interview/shared/:token/lobby" component={StandalonePage(LobbyPage)} />
      <Route
        path="/interview/shared/:token/interview"
        component={StandalonePage(SharedInterviewPage)}
      />
      <Route path="/interview/shared/:token/complete" component={StandalonePage(ThankYouPage)} />

      <Route path="/interview/invite/:token" component={StandalonePage(EmailEntryPage)} />
      <Route path="/interview/invite/:token/ready" component={StandalonePage(ReadyPage)} />
      <Route path="/interview/invite/:token/setup" component={SetupRedirect} />
      <Route path="/interview/invite/:token/lobby" component={StandalonePage(LobbyPage)} />
      <Route
        path="/interview/invite/:token/interview"
        component={StandalonePage(SharedInterviewPage)}
      />
      <Route path="/interview/invite/:token/complete" component={StandalonePage(ThankYouPage)} />

      <Route path="/apply/:token" component={StandalonePage(ApplyPage)} />

      <Route path="/company/:slug" component={StandalonePage(CompanySpacePage)} />
      <Route path="/cv/:token" component={StandalonePage(PublicCVPage)} />

      <Route path="/book/:token" component={StandalonePage(BookingPage)} />

      <Route path="/meet/:token" component={StandalonePage(CandidateMeetingPage)} />

      <Route path="/admin" component={() => <Navigate href="/admin/dashboard" />} />
      <Route path="/admin/login" component={StandalonePage(AdminLoginPage)} />
      <Route
        path="/admin/dashboard"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminDashboardPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/admin/users"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminUsersPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/admin/sessions/:sessionId"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminSessionReplayPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/admin/invites"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminInvitesPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/admin/workspaces"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminWorkspacesPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/app/invite/:token"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <Suspense fallback={<PageLoader />}>
                <FounderInviteAcceptPage />
              </Suspense>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/claim/:id"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <Suspense fallback={<PageLoader />}>
                <ClaimDemoPage />
              </Suspense>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/admin/workspaces/:id"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminWorkspaceDetailPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/admin/admins"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminManagementPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/admin/support"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminSupportPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/admin/health"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminHealthPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/admin/scoring"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminScoringPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/admin/livekit"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminLiveKitPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/admin/voice-engine"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminVoiceEnginePage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/admin/emails"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminEmailTemplatesPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/admin/email-logs"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminEmailLogsPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/admin/prompts"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminPromptsPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />
      <Route
        path="/admin/translations"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminTranslationsPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />

      <Route
        path="/admin/design-system"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminDesignSystemPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />

      <Route
        path="/admin/demo"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminDemoPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />

      <Route
        path="/admin/partner-keys"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminPartnerKeysPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />

      <Route
        path="/admin/storage"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminStoragePage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />

      <Route
        path="/admin/certifications"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminCertificationsPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />

      <Route
        path="/admin/trial-requests"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminTrialRequestsPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />

      <Route
        path="/admin/reminders"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminRemindersPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />

      <Route
        path="/admin/platform-settings"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminPlatformSettingsPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />

      <Route
        path="/admin/onboarding"
        component={() => (
          <I18nProvider>
            <ToastProvider>
              <ProtectedAdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminOnboardingPage />
                </Suspense>
              </ProtectedAdminRoute>
            </ToastProvider>
          </I18nProvider>
        )}
      />

      <Route path="*" component={StandalonePage(NotFoundPage)} />
    </>
  )
}

export { AppRoot, AppRoutes, withHiringManagerFullscreen }
